import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  cn,
  ScrollArea,
  ScrollBar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "web-design-system";
import { NOMINEE_RELATIONSHIP } from "creso-core/constants";
import { Dot, PencilIcon, Trash2Icon, UserIcon } from "lucide-react";
import { useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import { InvestorAddressDetails } from "creso-core/types";
import { ConditionalRenderer } from "../primitives";
import {
  NomineeEditDialog,
  NomineeFormValues,
  PercentageAllocation,
} from "./nominee-edit-dialog";
import { Section } from "./section";
import { useAccountForm } from "./context";

export function NomineeSection({
  openNomineeId,
  onOpenNomineeHandled,
}: {
  openNomineeId?: string | number;
  onOpenNomineeHandled?: () => void;
} = {}) {
  const { form, isEditing, data, updateChangeSet, client, onChangeSetUpdated } =
    useAccountForm();

  const [isSectionOpen, setIsSectionOpen] = useState(false);
  useEffect(() => {
    if (openNomineeId != null) setIsSectionOpen(true);
  }, [openNomineeId]);

  const isNomineeApplicable = useMemo(
    () => ["Y", "N"].includes(data.account?.nomination_opt ?? ""),
    [data.account?.nomination_opt]
  );

  // Get client address from form to pass to nominee dialogs
  const { address } = data;

  const { fields, update, remove } = useFieldArray({
    control: form.control,
    name: "nominees",
  });

  // Calculate total percentage used by all nominees
  const totalPercentageUsed = useMemo(() => {
    return fields.reduce(
      (sum, nominee) => sum + (nominee.applicable_percentage || 0),
      0
    );
  }, [fields]);

  const addNominee = async (
    values: NomineeFormValues,
    percentageAllocations: PercentageAllocation[]
  ) => {
    try {
      const nomineeChangeSet: Record<string, unknown>[] = fields.map(
        (field, index) => {
          const allocation = percentageAllocations[index];
          return {
            nominee_id: field.nominee_id,
            name: field.name,
            relationship: field.relationship,
            identity_type: field.identity_type,
            identity_number: field.identity_number,
            email: field.email,
            mobile: field.mobile,
            date_of_birth: field.date_of_birth,
            guardian_name: field.guardian_name,
            guardian_pan: field.guardian_pan,
            address_line_1: field.address_line_1,
            address_line_2: field.address_line_2,
            address_line_3: field.address_line_3,
            country: field.country,
            city: field.city,
            pin_code: field.pin_code,
            applicable_percentage:
              allocation?.applicable_percentage ?? field.applicable_percentage,
          };
        }
      );

      await updateChangeSet({
        INVESTMENT_ACCOUNT: {
          nomination_opt: "Y",
        },
        NOMINEE: [
          ...nomineeChangeSet,
          {
            name: values.name,
            relationship: values.relationship,
            identity_type: values.identity_type,
            identity_number: values.identity_number,
            email: values.email,
            mobile: values.mobile,
            date_of_birth: values.date_of_birth,
            guardian_name: values.guardian_name,
            guardian_pan: values.guardian_pan,
            address_line_1: values.address_line_1,
            address_line_2: values.address_line_2,
            address_line_3: values.address_line_3,
            country: values.country,
            city: values.city,
            pin_code: values.pin_code,
            applicable_percentage:
              percentageAllocations[percentageAllocations.length - 1]
                .applicable_percentage ?? values.applicable_percentage,
          },
        ],
      });

      if (client.status === "ACTIVE") {
        onChangeSetUpdated?.();
      }
      return true;
    } catch {
      toast.error("Failed to add nominee");
      return false;
    }
  };

  const updateNominee = async (
    values: NomineeFormValues,
    index: number,
    percentageAllocations: PercentageAllocation[]
  ) => {
    try {
      // Build the updated nominees array for the changeSet
      const nomineeChangeSet: Record<string, unknown>[] = fields.map(
        (field, i) => {
          // Use the new values for the updated nominee, keep existing values for others
          const nomineeData = i === index ? { ...field, ...values } : field;
          const allocation = percentageAllocations[i];
          return {
            nominee_id: nomineeData.nominee_id,
            name: nomineeData.name,
            relationship: nomineeData.relationship,
            identity_type: nomineeData.identity_type,
            identity_number: nomineeData.identity_number,
            email: nomineeData.email,
            mobile: nomineeData.mobile,
            date_of_birth: nomineeData.date_of_birth,
            guardian_name: nomineeData.guardian_name,
            guardian_pan: nomineeData.guardian_pan,
            address_line_1: nomineeData.address_line_1,
            address_line_2: nomineeData.address_line_2,
            address_line_3: nomineeData.address_line_3,
            country: nomineeData.country,
            city: nomineeData.city,
            pin_code: nomineeData.pin_code,
            applicable_percentage:
              allocation?.applicable_percentage ??
              nomineeData.applicable_percentage,
          };
        }
      );

      await updateChangeSet({
        INVESTMENT_ACCOUNT: {
          nomination_opt: "Y",
        },
        NOMINEE: nomineeChangeSet,
      });

      // Update all fields with new percentages
      fields.forEach((field, i) => {
        const allocation = percentageAllocations.find(
          (a) => a.nominee_id === field.nominee_id
        );
        update(i, {
          ...field,
          ...(i === index ? values : {}),
          applicable_percentage:
            allocation?.applicable_percentage ?? field.applicable_percentage,
        });
      });

      if (client.status === "ACTIVE") {
        onChangeSetUpdated?.();
      }

      return true;
    } catch {
      toast.error("Failed to update nominee");
      return false;
    }
  };

  const deleteNominee = (index: number) => {
    const removedPercentage = fields[index].applicable_percentage || 0;
    const remaining = fields.filter((_, i) => i !== index);

    if (remaining.length > 0 && removedPercentage > 0) {
      const extraEach = Math.floor(removedPercentage / remaining.length);
      const leftover = removedPercentage - extraEach * remaining.length;

      // Distribute the removed nominee's percentage to the others
      remaining.forEach((field, i) => {
        const originalIndex = i >= index ? i + 1 : i;
        update(originalIndex, {
          ...field,
          applicable_percentage:
            (field.applicable_percentage || 0) +
            extraEach +
            (i === 0 ? leftover : 0),
        });
      });
    }

    remove(index);
  };

  if (!isNomineeApplicable) return null;

  return (
    <Section
      title="Nominees"
      open={isSectionOpen}
      onOpenChange={setIsSectionOpen}
    >
      <div className="flex flex-col gap-4">
        {fields.length ? (
          <ScrollArea>
            <ScrollBar orientation="horizontal" />
            <div className="flex flex-row gap-4">
              {fields.map((nominee, index) => (
                <NomineeCard
                  key={nominee.id}
                  nominee={nominee}
                  onUpdate={(values, percentageAllocations) =>
                    updateNominee(values, index, percentageAllocations)
                  }
                  onDelete={() => deleteNominee(index)}
                  totalPercentageUsed={totalPercentageUsed}
                  clientAddress={address}
                  allNominees={fields}
                  defaultOpen={
                    openNomineeId != null &&
                    String(nominee.nominee_id) === String(openNomineeId)
                  }
                  onOpenChange={(open) => {
                    if (!open) onOpenNomineeHandled?.();
                  }}
                />
              ))}
            </div>
          </ScrollArea>
        ) : (
          <p className="text-sm text-muted-foreground">
            Does not wish to nominate
          </p>
        )}
        <NomineeEditDialog
          onSave={addNominee}
          totalPercentageUsed={totalPercentageUsed}
          clientAddress={address}
          allNominees={fields}
          mode="add"
          trigger={
            <Button
              variant="link"
              className={cn("hidden px-0 w-fit", !isEditing && "flex")}
              type="button"
            >
              + Add nominee
            </Button>
          }
        />
      </div>
    </Section>
  );
}

function NomineeCard({
  nominee,
  onUpdate,
  onDelete,
  totalPercentageUsed,
  clientAddress,
  allNominees,
  defaultOpen,
  onOpenChange,
}: {
  nominee: NomineeFormValues & { id: string };
  onUpdate: (
    values: NomineeFormValues,
    percentageAllocations: PercentageAllocation[]
  ) => Promise<boolean> | boolean;
  onDelete: () => void;
  totalPercentageUsed: number;
  clientAddress?: InvestorAddressDetails;
  allNominees: NomineeFormValues[];
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { isEditing, data } = useAccountForm();

  const savedNominee = data.nominee?.find((n) => n.id === nominee.nominee_id);

  const relationshipLabel = NOMINEE_RELATIONSHIP.find(
    (r) => r.value === nominee.relationship
  )?.label;

  const cardContent = (
    <>
      <div className="size-10 bg-card rounded-lg items-center justify-center flex">
        <UserIcon className="size-5" />
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold truncate">{nominee.name}</span>
          <ConditionalRenderer condition={!savedNominee}>
            <Badge
              variant="custom"
              className="text-xs text-tag-green-foreground bg-tag-green flex-shrink-0"
            >
              New
            </Badge>
          </ConditionalRenderer>
        </div>

        <div className="text-sm flex items-center text-muted-foreground mt-1">
          {relationshipLabel}
          <Dot />
          {nominee.applicable_percentage}% share
        </div>
      </div>

      <ConditionalRenderer condition={isEditing}>
        <div className="absolute top-2 right-2 flex flex-row gap-1 items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <NomineeEditDialog
                data={nominee}
                onSave={onUpdate}
                totalPercentageUsed={totalPercentageUsed}
                clientAddress={clientAddress}
                allNominees={allNominees}
                mode={isEditing ? "update" : "view"}
                defaultOpen={defaultOpen}
                onOpenChange={onOpenChange}
                trigger={
                  <Button
                    size="icon"
                    variant="ghost"
                    className={cn("invisible", isEditing && "visible")}
                    type="button"
                  >
                    <PencilIcon className="size-4" />
                  </Button>
                }
              />
            </TooltipTrigger>
            <TooltipContent>Edit</TooltipContent>
          </Tooltip>

          <ConditionalRenderer condition={!savedNominee}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={onDelete}
                  variant="ghost"
                  size="icon"
                  type="button"
                >
                  <Trash2Icon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </ConditionalRenderer>
        </div>
      </ConditionalRenderer>
    </>
  );

  // When not editing, wrap card in dialog trigger for view mode
  if (!isEditing) {
    return (
      <NomineeEditDialog
        data={nominee}
        onSave={onUpdate}
        totalPercentageUsed={totalPercentageUsed}
        clientAddress={clientAddress}
        allNominees={allNominees}
        mode={isEditing ? "update" : "view"}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        trigger={
          <button
            type="button"
            className="flex hover:shadow-lg transition-all items-start text-primary gap-3 w-80 p-4 relative rounded-lg border border-border cursor-pointer text-left"
          >
            {cardContent}
          </button>
        }
      />
    );
  }

  return (
    <div className="flex items-start text-primary gap-3 w-80 p-4 relative rounded-lg border border-border">
      {cardContent}
    </div>
  );
}
